import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, message, Modal, Space, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
// Components
import { Filters } from "@/components/filters";
// Hooks
import { getRootStore } from "@/stores/RootStore";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
// === UTILITY FUNCTIONS ===
// Styles
import { DataTable } from "@/components/DataTable";
import styles from "@/styles/WorkShiftsPage.module.less";
import { WorkShiftStatusTag } from "./WorkShiftStatusTag";
const { Title } = Typography;
/**
 * WorkShiftsPage Component
 * Following SOLID principles:
 * - Single Responsibility: Only handles page layout and orchestration
 * - Open/Closed: Easy to extend with new features
 * - Dependency Inversion: Uses abstractions (hooks) instead of direct implementations
 */
const WorkShiftsPage = observer(() => {
    const { workShiftsStore } = getRootStore();
    const { dataTableStore, filtersStore } = workShiftsStore;
    const navigate = useNavigate();
    // Load data on component mount
    useEffect(() => {
        workShiftsStore.init();
    }, []);
    // Auto-apply filters when filter store values change
    useEffect(() => {
        dataTableStore.setFilters(filtersStore.values);
        dataTableStore.loadData();
    }, [filtersStore.values]);
    // === EVENT HANDLERS ===
    const handleViewWorkShift = (shiftId) => {
        navigate(`/workshifts/${shiftId}`);
    };
    const handleCreateWorkShift = () => {
        navigate("/workshifts/new");
    };
    const handleDeleteWorkShift = (shiftId) => {
        Modal.confirm({
            title: "Удаление смены",
            content: "Вы уверены, что хотите удалить эту смену?",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            onOk: async () => {
                try {
                    await workShiftsStore.deleteWorkShift(shiftId);
                    message.success("Смена успешно удалена");
                }
                catch (error) {
                    message.error("Ошибка при удалении смены");
                }
            },
        });
    };
    const handleCopyWorkShiftNextDay = async (shiftId) => {
        try {
            await workShiftsStore.copyWorkShiftNextDay(shiftId);
            message.success("Смена успешно скопирована на завтра");
        }
        catch (error) {
            message.error("Ошибка при копировании смены на завтра");
        }
    };
    // === TABLE CONFIGURATION ===
    const columns = [
        {
            title: "ID смены",
            dataIndex: "id",
            key: "id",
            fixed: "left",
            width: 100,
            render: (text, record) => (_jsx(Button, { type: "link", onClick: () => handleViewWorkShift(record.id), className: styles.shiftIdLink, children: text.slice(-8) })),
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => (_jsx(WorkShiftStatusTag, { status: status })),
        },
        {
            title: "Техник",
            dataIndex: "idTechnician",
            key: "idTechnician",
            width: 150,
            render: (idTechnician) => (_jsx("span", { children: idTechnician }) // TODO: Replace with technician name
            ),
        },
        {
            title: "Плановое время",
            key: "plannedTime",
            width: 200,
            render: (_, record) => (_jsxs("div", { children: [_jsxs("div", { children: [_jsx("strong", { children: "\u041D\u0430\u0447\u0430\u043B\u043E:" }), " ", formatDateAndTime(record.plannedStartTime)] }), _jsxs("div", { children: [_jsx("strong", { children: "\u041A\u043E\u043D\u0435\u0446:" }), " ", formatDateAndTime(record.plannedEndTime)] })] })),
            sorter: (a, b) => a.plannedStartTime.unix() - b.plannedStartTime.unix(),
        },
        {
            title: "Фактическое время",
            key: "actualTime",
            width: 200,
            render: (_, record) => (_jsxs("div", { children: [record.actualStartTime && (_jsxs("div", { children: [_jsx("strong", { children: "\u041D\u0430\u0447\u0430\u043B\u043E:" }), " ", formatDateAndTime(record.actualStartTime)] })), record.actualEndTime && (_jsxs("div", { children: [_jsx("strong", { children: "\u041A\u043E\u043D\u0435\u0446:" }), " ", formatDateAndTime(record.actualEndTime)] })), !record.actualStartTime && !record.actualEndTime && (_jsx("span", { style: { color: "#999" }, children: "\u041D\u0435 \u0437\u0430\u0434\u0430\u043D\u043E" }))] })),
        },
        {
            title: "Длительность",
            key: "duration",
            width: 120,
            render: (_, record) => {
                const startTime = record.actualStartTime || record.plannedStartTime;
                const endTime = record.actualEndTime || record.plannedEndTime;
                const duration = endTime.diff(startTime, "hours", true);
                return `${duration.toFixed(1)} ч`;
            },
        },
        {
            title: "Действия",
            key: "actions",
            fixed: "right",
            width: 200,
            render: (_, record) => (_jsxs(Space, { size: "small", children: [_jsx(Tooltip, { title: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u0430 \u0437\u0430\u0432\u0442\u0440\u0430", children: _jsx(Button, { type: "text", icon: _jsx(CopyOutlined, {}), onClick: () => handleCopyWorkShiftNextDay(record.id), style: { color: "#1890ff" } }) }), _jsx(Tooltip, { title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C", children: _jsx(Button, { type: "text", danger: true, icon: _jsx(DeleteOutlined, {}), onClick: () => handleDeleteWorkShift(record.id) }) })] })),
        },
    ];
    // === RENDER ===
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.pageHeader, children: [_jsx(Title, { level: 2, className: styles.title, children: "\u0423\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u0441\u043C\u0435\u043D\u0430\u043C\u0438" }), _jsx(Space, { children: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: handleCreateWorkShift, children: "\u041D\u043E\u0432\u0430\u044F \u0441\u043C\u0435\u043D\u0430" }) })] }), _jsx(Filters, { filtersStore: filtersStore, title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0438 \u043F\u043E\u0438\u0441\u043A", showClearAll: true, showApplyButton: false }), _jsx(DataTable, { store: dataTableStore, config: { columns, rowKey: "id" } })] }));
});
WorkShiftsPage.displayName = "WorkShiftsPage";
export default WorkShiftsPage;
